import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Shield, DollarSign, Users } from 'lucide-react';
import { useRef } from 'react';
import { MagneticButton } from './InteractiveEffects';
import CompanyProfilePDF from './CompanyProfilePDF';

const Hero = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"]
  });
  
  // Parallax transforms
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const trustBadges = [
    { icon: Shield, label: 'Canadian Expertise' },
    { icon: DollarSign, label: 'Affordable Pricing' },
    { icon: Users, label: 'Professional Team' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
    },
  };

  return (
    <section 
      ref={sectionRef}
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image with Parallax Effect */}
      <motion.div 
        className="absolute inset-0"
        style={{ y: backgroundY }}
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.5, ease: 'easeOut' }}
      >
        <img
          src="https://images.pexels.com/photos/4067521/pexels-photo-4067521.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Modern glass building"
          className="w-full h-full object-cover scale-110"
        />
        <div className="absolute inset-0 hero-overlay"></div>
      </motion.div>

      {/* Animated Background Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-sky-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0.2, 0.5, 0.2],
            }}
            transition={{
              duration: 5 + Math.random() * 5,
              repeat: Infinity,
              delay: Math.random() * 5,
            }}
          />
        ))}
      </div>

      {/* Content with Parallax */}
      <motion.div 
        className="relative z-10 max-w-7xl mx-auto px-6 py-32 md:py-40"
        style={{ y: textY, opacity }}
      >
        <motion.div 
          className="max-w-3xl"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Overline */}
          <motion.div className="mb-6" variants={itemVariants}>
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-sky-700 inline-flex items-center gap-2">
              <motion.span 
                className="w-8 h-px bg-sky-600"
                initial={{ width: 0 }}
                animate={{ width: 32 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              />
              UAE's Trusted Contractor
            </span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter text-sky-900 mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            variants={itemVariants}
          >
            Premium Aluminium & Glass Solutions in UAE
          </motion.h1>

          {/* Subheadline */}
          <motion.p 
            className="text-lg sm:text-xl text-sky-700 mb-8 leading-relaxed max-w-xl"
            variants={itemVariants}
          >
            Original Canadian Quality at Competitive Prices. We deliver excellence in aluminium works, glass installations, and maintenance services.
          </motion.p>

          {/* CTA Buttons with Magnetic Effect */}
          <motion.div className="flex flex-wrap gap-4 mb-12" variants={itemVariants}>
            <MagneticButton>
              <motion.a
                href="https://wa.me/971566832840?text=Hello%2C%20I%27m%20interested%20in%20your%20services"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp group"
                data-testid="hero-whatsapp-btn"
                whileHover={{ scale: 1.05, boxShadow: '0 10px 40px rgba(37, 211, 102, 0.4)' }}
                whileTap={{ scale: 0.98 }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Chat on WhatsApp
                <motion.span
                  className="ml-1"
                  animate={{ x: [0, 4, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  →
                </motion.span>
              </motion.a>
            </MagneticButton>
            <MagneticButton>
              <CompanyProfilePDF variant="hero" />
            </MagneticButton>
            <MagneticButton>
              <motion.a
                href="#services"
                className="btn-outline group"
                data-testid="hero-services-btn"
                whileHover={{ scale: 1.05, backgroundColor: 'rgba(255,255,255,0.1)' }}
                whileTap={{ scale: 0.98 }}
              >
                View Services
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </motion.a>
            </MagneticButton>
          </motion.div>

          {/* Trust Badges */}
          <motion.div className="flex flex-wrap gap-4" variants={itemVariants}>
            {trustBadges.map((badge, index) => (
              <motion.div
                key={badge.label}
                className="trust-badge px-4 py-2 flex items-center gap-2 rounded-sm cursor-default"
                data-testid={`trust-badge-${badge.label.toLowerCase().replace(/\s+/g, '-')}`}
                whileHover={{ 
                  scale: 1.05, 
                  backgroundColor: 'rgba(3, 105, 161, 0.1)',
                  borderColor: 'rgba(3, 105, 161, 0.25)'
                }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1 + index * 0.1 }}
              >
                <badge.icon size={16} className="text-sky-600" />
                <span className="text-sm text-sky-700 font-medium">{badge.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:block"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.5 }}
      >
        <motion.div 
          className="w-6 h-10 border-2 border-sky-400/50 rounded-full flex justify-center cursor-pointer"
          whileHover={{ borderColor: 'rgba(56, 189, 248, 0.7)' }}
          onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
        >
          <motion.div 
            className="w-1.5 h-3 bg-sky-500/60 rounded-full mt-2"
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
